import AccordionBox from "../../../../../../components/AccordionBox";
import {
  RazorButton,
  RazorDateTime,
  RazorImageFallback,
  RazorInputField,
  toast,
} from "../../../../../../components/razor-reuse";
import ZoomableImage from "../../../../../../components/fragment/image/zoomable-image";
import { useOverviewContext } from "../../../CustomerSingle";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../../../../redux/type";
import CustomModal, {
  useDialogToggle,
} from "../../../../../../components/fragment/custom-modal";
import { systemRoles } from "../../../../../../utils/roles";
import { useEffect, useState } from "react";
import {
  addCommentToDocument,
  getCustomerAdditionalDoc,
  getCustomerSingle,
  uploadCustomerDocument,
} from "../../../../../../redux/customer";
import { Loader2, Plus } from "lucide-react";
import {
  convertToBase64,
  reactSelectStyleTable,
} from "../../../../../../utils/Helper";

const documentCategoryOptions = [
  { label: "Source of Funds", value: "sof" },
  { label: "Additional Document", value: "additional_doc" },
];

const documentOptions = [
  { label: "Pay Slip", value: "PAY_SLIP" },
  { label: "Bank Statement", value: "BANK_STATEMENT" },
  { label: "Tax Return", value: "TAX_RETURN" },
  { label: "Investment Certificates", value: "INVESTMENT_CERTIFICATES" },
  { label: "Contract Notes", value: "CONTRACT_NOTES" },
  { label: "Signed Will", value: "SIGNED_WILL" },
  { label: "Gift Declaration", value: "GIFT_DECLARATION" },
  { label: "Loan Agreement", value: "LOAN_AGREEMENT" },
  { label: "Signed Sales Contract", value: "SIGNED_SALES_CONTRACT" },
  {
    label: "Property Ownership Documents",
    value: "PROPERTY_OWNERSHIP_DOCUMENTS",
  },
  { label: "Driver's License", value: "DRIVERS_LICENSE" },
  { label: "Passport", value: "PASSPORT" },
  { label: "National ID", value: "NATIONAL_ID" },
  {
    label: "Employment or Service Contract",
    value: "EMPLOYMENT_OR_SERVICE_CONTRACT",
  },
  { label: "Salary Account Statement", value: "SALARY_ACCOUNT_STATEMENT" },
  {
    label: "Business Registration or Incorporation Certificate",
    value: "BUSINESS_REGISTRATION_OR_INCORPORATION_CERTIFICATE",
  },
  { label: "Others", value: "OTHERS" },
];

// Document types that belong to the "Source of Funds" category — everything
// else in documentOptions is an "Additional Document" type.
const SOF_DOCUMENT_TYPE_VALUES = [
  "EMPLOYMENT_OR_SERVICE_CONTRACT",
  "SALARY_ACCOUNT_STATEMENT",
  "PAY_SLIP",
  "OTHERS",
  "BUSINESS_REGISTRATION_OR_INCORPORATION_CERTIFICATE",
];

const documentTypeOptionsByCategory: Record<string, typeof documentOptions> = {
  sof: documentOptions.filter((option) =>
    SOF_DOCUMENT_TYPE_VALUES.includes(option.value),
  ),
  additional_doc: documentOptions.filter(
    (option) => !SOF_DOCUMENT_TYPE_VALUES.includes(option.value),
  ),
};

export default function ResubmissionAccord() {
  const dispatch = useDispatch();
  const context = useOverviewContext();
  const cust_id = localStorage?.getItem("cors_key") || "";
  const [comment, setcomment] = useState({
    content: "",
    name: "",
    id: "",
  });

  const [uploadDoc, setuploadDoc] = useState({
    comments: "",
    documents: [],
    document_name: "",
    category: "",
    document_type: "",
    user: "",
  });

  // modal trigger view comment
  const { openDialog, closeDialog, dialogRef } = useDialogToggle();
  // modal trigger create comment
  const {
    openDialog: openCreateDialog,
    closeDialog: closeCreateDialog,
    dialogRef: createRef,
  } = useDialogToggle();
  // uplade document
  const {
    openDialog: openUploadeDialog,
    closeDialog: closeUploadeDialog,
    dialogRef: uploadRef,
  } = useDialogToggle();

  const { user } = useSelector((state: RootState) => state.auth);
  const {
    customer_additional_doc,
    loading_additional_doc,
    creating_comment,
    uploading_document,
  } = useSelector((state: RootState) => state.customer);
  const additionalDoc = customer_additional_doc || [];

  useEffect(() => {
    dispatch(getCustomerAdditionalDoc(cust_id) as any);
  }, []);

  const triggerAddComment = (id: string, name: string, content?: string) => {
    if (content) {
      openDialog();
      setcomment({
        content,
        name,
        id,
      });
    } else {
      openCreateDialog();
      setcomment({
        content: "",
        name,
        id,
      });
    }
  };
  const handleCreateComment = async () => {
    const data = await dispatch(
      addCommentToDocument({
        id: comment.id,
        body: { comment: comment.content },
      }) as any
    );
    if (data?.payload?.data?.status) {
      closeCreateDialog();
      dispatch(getCustomerAdditionalDoc(cust_id) as any);
    }
  };

  const handleUploadDoc = async () => {
    if (
      !uploadDoc.document_name ||
      !uploadDoc.category ||
      !uploadDoc.document_type ||
      uploadDoc.documents?.length < 1
    ) {
      toast.warning("Please provide required document");
      return;
    }

    const base64Files = await Promise.all(
      uploadDoc.documents.map(async (file: File) => await convertToBase64(file)) // This includes the data:image/png;base64, prefix))
    );
    const payload = {
      ...uploadDoc,
      documents: base64Files,
    };

    const data = await dispatch(
      uploadCustomerDocument({
        id: uploadDoc.user,
        body: payload,
      }) as any
    );
    if (data?.payload?.data?.status) {
      closeUploadeDialog();
      dispatch(getCustomerSingle({ id: cust_id }) as any);
      dispatch(getCustomerAdditionalDoc(cust_id) as any);
    }
  };

  const handleFileChange = (e: any) => {
    const selectedFiles = Array.from(e.target.files);
    setuploadDoc((prev) => ({ ...prev, documents: selectedFiles as [] }));
  };
  return (
    <>
      <AccordionBox defaultState={false} title="Additional Documents">
        <main className="bg-white min-h-[500px] p-[24px] space-y-[18px]">
          {/* Proof of Address */}
          {loading_additional_doc ? (
            <div className="h-[300px] grid place-content-center">
              <Loader2 className="size-[75px] text-pry animate-spin " />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-[20px]">
                <p className="text-[16px] text-[#758089]">
                  View and manage all uploaded documents
                </p>
                {user?.permissions?.includes(systemRoles[39]) && (
                  <button
                    onClick={() => {
                      openUploadeDialog();
                      setuploadDoc((prev) => ({ ...prev, user: cust_id }));
                    }}
                    className="text-[14px] bg-primary text-white py-[6px] px-[10px] rounded-[8px] inline-flex gap-[4px] items-center"
                  >
                    <Plus className="text-white size-[20px]" />
                    Upload Document
                  </button>
                )}
              </div>
              <div className="border border-[#e6e6e6] rounded-lg pb-[60px]">
                <div className=" mt-[8px]">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-[30px] justify-around">
                    {additionalDoc?.length === 0 ? (
                      <div className="space-y-[8px] mx-auto">
                        <div className="h-[260px] w-[285px] bg-gray-03 grid place-content-center overflow-hidden profile-img-box">
                          <RazorImageFallback loadSize={45} url={" "}>
                            <figure className="size-full text-gray-02">
                              No file found
                            </figure>
                          </RazorImageFallback>
                        </div>
                        <div className="text-gray-02 text-[12px] space-y-[12px] mt-8">
                          <p className="">No upload</p>
                          <p className="inline-flex items-center">
                            Date Uploaded: ---
                          </p>
                        </div>
                      </div>
                    ) : (
                      additionalDoc?.map((item: any, idx: number) => (
                        <div
                          key={idx}
                          className="space-y-[8px] mx-auto border px-[4px] pb-[4px] rounded-[8px]"
                        >
                          {item?.metadata?.fileType === "pdf" ||
                          item?.file_path?.[1]?.includes("/raw") ||
                          item?.file_path?.[1]?.includes(".pdf") ? (
                            <div className="h-[260px] w-[285px] bg-gray-03 grid place-content-center overflow-hidden text-center">
                              <div>
                                <div
                                  onClick={() => {
                                    const link = document.createElement("a");
                                    link.href = item?.file_path?.[1];
                                    link.download = item.metadata.fileName;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                >
                                  <span className="text-gray-01 underline cursor-pointer">
                                    Click to Download
                                  </span>
                                </div>
                                <br />
                                <span className="text-gray-01 underline cursor-pointer">
                                  {item?.document_sub_type}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <ZoomableImage
                              src={item?.file_path?.[1]}
                              alt={item?.document_type}
                              width={400}
                              height={450}
                              zoomLevel={1.3}
                              zoomWindowSize={400}
                              onClick={() => {
                                context?.setDocumentModal({
                                  visible: true,
                                  url: item?.file_path?.[1],
                                  doc_name:
                                    item?.metadata?.document_name ||
                                    item?.metadata?.fileName,
                                  file_name: item?.document_type,
                                  file_sizee: item?.metadata?.fileSize,
                                  status: item?.status,
                                  date: item?.updated_at,
                                });
                              }}
                              containerClassName="h-[260px] w-[285px] bg-gray-03 grid place-content-center overflow-hidden profile-img-box"
                            />
                          )}

                          <div className="text-gray-02 text-[12px] space-y-[12px] mt-8">
                            <p className="">
                              {item?.metadata?.document_name ||
                                item?.document_sub_type ||
                                "****"}
                            </p>
                            <p className="inline-flex items-center">
                              Date Uploaded:{" "}
                              {
                                <RazorDateTime
                                  date={item?.updated_at}
                                  hideAgo
                                  className="[&_span]:!text-[14px] [&_span]:ml-[4px] [&_span]:!text-gray-01"
                                />
                              }
                            </p>
                            <button
                              onClick={() => {
                                triggerAddComment(
                                  item?.id,
                                  item?.document_sub_type,
                                  item?.comments?.comments?.[0]?.text
                                );
                              }}
                              className="text-[12px] text-primary border border-primary py-[6px] px-[18px] rounded-[8px] block mx-auto"
                            >
                              {item?.comments?.comments?.length > 0
                                ? "View"
                                : "Add"}{" "}
                              Comment
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
        <CustomModal
          ref={dialogRef}
          onClose={() => {
            closeDialog();
            setcomment({
              content: "",
              name: "",
              id: "",
            });
          }}
          headerTitle="View Comment"
          containerClass="!w-[460px]"
        >
          <section className="mx-[28px] my-[24px] p-[10px] text-[14px] bg-white min-h-[78px] text-[#758089]">
            * {comment.content}
          </section>
        </CustomModal>

        {/* create comment */}
        <CustomModal
          ref={createRef}
          onClose={() => {
            closeCreateDialog();
            setcomment({
              content: "",
              name: "",
              id: "",
            });
          }}
          headerTitle="Add Comment"
          containerClass="!w-[460px]"
        >
          <section className="mx-[18px] my-[24px] p-[10px] text-[14px] min-h-[78px] text-[#758089]">
            <p className="text-[16px]">
              Add remarks for : {comment.name || "document file"}
            </p>
            <RazorInputField
              color="black-light"
              className="mt-[10px] "
              selectClassName={"!bg-red-200"}
              textareaRow={3}
              type="textarea"
              placeholder="Enter Comment"
              disabled={false}
              onChange={(e: any) => {
                const { value, name } = e.target;
                setcomment((prev) => ({ ...prev, [name]: value }));
              }}
              name="content"
              value={comment.content}
              capitalize
            />

            <div className="flex items-center gap-[6px] mt-[14px]">
              <RazorButton
                className="btn btn-big"
                label="Add Comment"
                color="black-light"
                onClick={handleCreateComment}
                disabled={creating_comment}
                loading={creating_comment}
              />
            </div>
          </section>
        </CustomModal>

        <CustomModal
          ref={uploadRef}
          onClose={() => {
            closeUploadeDialog();
            setuploadDoc({
              comments: "",
              documents: [],
              document_name: "",
              category: "",
              document_type: "",
              user: "",
            });
          }}
          headerTitle="Upload Document"
        >
          <section className="mx-[28px] my-[14px] p-[10px] text-[14px] min-h-[78px] text-[#758089]">
            <p className="text-[14px] text-[#2C3035] font-semibold">
              Add a new document to this user’s profile. Supported formats: PDF,
              JPEG, PNG (max 10MB)
            </p>

            <div className="space-y-[20px]">
              <RazorInputField
                color="black-light"
                className="mt-[10px] "
                selectClassName={"!bg-red-200"}
                textareaRow={3}
                type="text"
                placeholder="Enter Name"
                label="Enter Document Name"
                disabled={false}
                onChange={(e: any) => {
                  const { value, name } = e.target;
                  setuploadDoc((prev) => ({ ...prev, [name]: value }));
                }}
                name="document_name"
                value={uploadDoc.document_name}
                capitalize
              />

              <RazorInputField
                color="black-light"
                type="select"
                label="Document Category"
                selectStyles={reactSelectStyleTable}
                selectOption={documentCategoryOptions}
                selectValue={uploadDoc.category}
                onChange={(e: any) => {
                  setuploadDoc((prev) => ({
                    ...prev,
                    category: e?.value,
                    // the type list depends on category, so a type picked
                    // under the old category may no longer be valid
                    document_type: "",
                  }));
                }}
                disabled={false}
                style={{ position: "relative", zIndex: 8 }}
                menuPlacement={"bottom"}
              />

              <RazorInputField
                color="black-light"
                type="select"
                label="Document Type"
                selectStyles={reactSelectStyleTable}
                selectOption={documentTypeOptionsByCategory[uploadDoc.category] || []}
                selectValue={uploadDoc.document_type}
                onChange={(e: any) => {
                  setuploadDoc((prev) => ({
                    ...prev,
                    document_type: e?.value,
                  }));
                }}
                disabled={!uploadDoc.category}
                placeholder={
                  uploadDoc.category
                    ? "Select Document Type"
                    : "Select a category first"
                }
                style={{ position: "relative", zIndex: 7 }}
                menuPlacement={"bottom"}
              />

              <div className="relative">
                <label
                  htmlFor="documents"
                  className="text-[12px] text-[#020202] mb-[8px]"
                >
                  File Upload
                </label>
                <div className="border-[0.8px] w-full items-center flex h-[49px] rounded-[8px] border-[#ccc] px-[12px]">
                  <input
                    id="documents"
                    type="file"
                    accept=".png, .jpg, .jpeg, .pdf"
                    multiple
                    onChange={handleFileChange}
                  />
                </div>

                {uploadDoc.documents.length > 0 && (
                  <ul
                    style={{ marginTop: "10px" }}
                    className="list-disc list-inside px-[4px]"
                  >
                    {uploadDoc.documents.map((file: any, index: number) => (
                      <li key={index}>{file?.name}</li>
                    ))}
                  </ul>
                )}
              </div>

              <RazorInputField
                color="black-light"
                className="mt-[10px] "
                textareaRow={3}
                type="textarea"
                placeholder="Enter comment"
                label="Comment (Optional)"
                disabled={false}
                onChange={(e: any) => {
                  const { value, name } = e.target;
                  setuploadDoc((prev) => ({ ...prev, [name]: value }));
                }}
                name="comments"
                value={uploadDoc.comments}
                capitalize
              />
            </div>

            <div className="flex items-center gap-[6px] mt-[14px]">
              <RazorButton
                className="btn btn-big"
                label="Upload Document"
                color="black-light"
                onClick={handleUploadDoc}
                disabled={
                  !uploadDoc.document_name ||
                  !uploadDoc.category ||
                  !uploadDoc.document_type ||
                  uploadDoc.documents?.length < 1 ||
                  uploading_document
                }
                loading={uploading_document}
              />
            </div>
          </section>
        </CustomModal>
      </AccordionBox>
    </>
  );
}
